import { FC, useEffect, useRef, useState } from 'react';

import { useApplication } from '@eneco/common/application';
import env from '@eneco/common/env';
import logger from '@eneco/common/log';
import { toURL } from '@eneco/common/path';
import type { LayoutServiceExperimentInfo } from '@sitecore/types/lib';
import Script from 'next/script';

import { DataLayerEntry } from '../';
import { GTM_PLATFORM_VERSION } from './constants';
import { useEnrichedCustomerData } from './hooks/useEnrichedCustomerData';
import { useGTM } from './hooks/useGTM';
import { useExperimentTracking } from './hooks/useSitecoreExperimentTracking';
import { getEncodedCustomerId } from './utils/encodeCustomerId';
import { isFirstPartyGtmHost } from './utils/isFirstPartyGtmHost';

export type Props = {
  eventsFromSSR?: DataLayerEntry[];
  experimentInfo?: LayoutServiceExperimentInfo;
  customerId?: string;
  accountId?: number | null;
  businessUnit?: string;
  label?: string;
};

export const GTM: FC<Props> = args => {
  const GTM_ID = env('GTM_ID');
  const { searchParams } = useApplication();
  const isNL = args.businessUnit === 'nl';
  if (!isNL) {
    logger.error('GTM is not supported for business unit:', `${args.businessUnit}`);
    return null;
  }
  if (!GTM_ID || searchParams.get('DisableGTM') === '1') return null;
  return <GTMInner {...args} gtmId={GTM_ID} />;
};

export const GTMInner: FC<
  Props & {
    gtmId: string;
  }
> = ({ eventsFromSSR, customerId, accountId, gtmId, experimentInfo, businessUnit, label }) => {
  const GTM_URL = isFirstPartyGtmHost()
    ? toURL(`/analytics/gtm.js?id=${gtmId}`)
    : `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  const { pushInitialDataLayerEvents, getIsInitialized, setIsInitialized, pushToDataLayer } = useGTM();
  const [encodedCustomerId, setEncodedCustomerId] = useState<string | undefined>(undefined);
  // Track which experiment IDs have already been pushed to avoid duplicate events
  // when the user navigates back to the same experiment page within a session.
  const trackedExperimentsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const addInitialEvents = async () => {
      const encodedCustomerId = await getEncodedCustomerId(customerId);
      setEncodedCustomerId(encodedCustomerId);
      if (getIsInitialized()) return;
      setIsInitialized(true);
      const initialEvents: DataLayerEntry[] = [
        {
          clientId: customerId ? `${customerId}` : undefined,
          clientIdEncoded: encodedCustomerId,
          platformVersion: GTM_PLATFORM_VERSION,
        },
        { 'gtm.start': new Date().getTime(), event: 'gtm.js' },
      ];
      eventsFromSSR?.forEach(event => {
        initialEvents.push(event);
        const experimentId = (event as { experiment_id?: string }).experiment_id;
        if (experimentId) trackedExperimentsRef.current.add(experimentId);
      });
      pushInitialDataLayerEvents(initialEvents);
    };
    addInitialEvents();
    // eventsFromSSR is intentionally excluded: initial-load events are handled once;
    // subsequent navigation events are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIsInitialized, setIsInitialized, pushInitialDataLayerEvents, customerId]);

  // Push server-side events on subsequent client-side navigations (GTM already initialized).
  const previousEventsFromSSRRef = useRef(eventsFromSSR);
  useEffect(() => {
    if (!getIsInitialized()) return;
    if (previousEventsFromSSRRef.current === eventsFromSSR) return;
    previousEventsFromSSRRef.current = eventsFromSSR;
    eventsFromSSR?.forEach(event => {
      const experimentId = (event as { experiment_id?: string }).experiment_id;
      if (experimentId) {
        if (trackedExperimentsRef.current.has(experimentId)) return;
        trackedExperimentsRef.current.add(experimentId);
      }
      pushToDataLayer(event);
    });
  }, [eventsFromSSR, getIsInitialized, pushToDataLayer]);

  useEnrichedCustomerData({
    customerId,
    accountId,
    businessUnit,
    label,
    encodedCustomerId,
    isInitialized: getIsInitialized(),
  });

  useExperimentTracking(experimentInfo);

  return <Script strategy="afterInteractive" src={GTM_URL} id="gtm-script" data-testid="gtm-script" />;
};
